import { useEffect, useRef, useState } from 'react';
import { focusManager } from '../FocusManager';

interface UseFocusManagerOptions {
    componentId: string;
    enabled?: boolean;
}

export const useFocusManager = <T extends HTMLElement = HTMLElement>(options: UseFocusManagerOptions) => {
    const { componentId, enabled = true } = options;
    const elementRef = useRef<T | null>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!enabled || !elementRef.current) return;

        const element = elementRef.current;

        const handleFocusIn = (_: FocusEvent) => {
            setIsFocused(true);
            focusManager.setFocus(componentId, element);
        };

        const handleFocusOut = (event: FocusEvent) => {
            if (!element.contains(event.relatedTarget as Node)) {
                setIsFocused(false);
                focusManager.unfocus(componentId);
            }
        };

        element.addEventListener('focusin', handleFocusIn);
        element.addEventListener('focusout', handleFocusOut);

        if (document.activeElement && element.contains(document.activeElement)) {
            handleFocusIn({} as FocusEvent);
        }

        return () => {
            element.removeEventListener('focusin', handleFocusIn);
            element.removeEventListener('focusout', handleFocusOut);

            if (isFocused) {
                focusManager.unfocus(componentId);
            }
        };
    }, [componentId, enabled]);

    useEffect(() => {
        if (!enabled || !elementRef.current) return;

        const element = elementRef.current;

        // tabindex может быть не установлен на элементе что был передан в useFocusManager
        // для того чтобы програмно его можно было фокусировать добавляется -1 если tabindex не задан
        if (!element.hasAttribute('tabindex')) {
            element.setAttribute('tabindex', '-1');
        }
    }, [enabled]);

    useEffect(() => {
        if (!isFocused) {
            return;
        }
        if (!enabled || !elementRef.current) return;

        const handleWindowFocus = () => {
            //  возвращаем фокус на активный элемент если он был потерян при возвращении на вкладку
            if (!document.activeElement || !elementRef.current?.contains(document.activeElement)) {
                elementRef.current?.focus();
            }
        };
        window.addEventListener('focus', handleWindowFocus);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [componentId, enabled, isFocused]);

    return {
        ref: elementRef,
        isFocused,
    };
};
